<script lang="ts">
import { RouteLocationRaw } from 'vue-router';

export interface Row {
    label: string;
    value?: string;
    valueOverride?: {
      component: any,
      props?: Object
    },
    to?: RouteLocationRaw;
    dataTestid?: string;
    valueDataTestid?: string;
    status?: 'success' | 'warning' | 'info' | 'error',
}

export interface MetadataProps {
  rows: Row[];
}
</script>

<script setup lang="ts">
const { rows } = defineProps<MetadataProps>();

const getRowValueId = (row:Row): string => `value-${ row.label }:${ row.value }`.toLowerCase().replaceAll(' ', '');
</script>

<template>
  <div class="identifying-information">
    <div
      v-for="row in rows"
      :key="`${row.label}:${row.value}`"
      class="metadata-row"
      :data-testid="row.dataTestid"
    >
      <label
        class="label text-deemphasized"
        :for="getRowValueId(row)"
      >
        {{ row.label }}
      </label>
      <!-- A custom component specified as an object, responsible for it's own styling -->
      <div
        v-if="typeof row.valueOverride?.component !== 'string' && row.valueOverride?.component && row.value"
        :id="getRowValueId(row)"
        class="full-custom-value"
      >
        <component
          :is="row.valueOverride?.component"
          v-if="row.valueOverride?.component"
          v-bind="row.valueOverride?.props"
          :data-testid="row.valueDataTestid"
        />
      </div>
      <!-- A formatter with a component specified as a string -->
      <div
        v-else-if="row.valueOverride?.component && row.value"
        :id="getRowValueId(row)"
        class="value"
      >
        <component
          :is="row.valueOverride?.component"
          v-if="row.valueOverride?.component"
          v-bind="row.valueOverride?.props"
          :data-testid="row.valueDataTestid"
        />
      </div>
      <div
        v-else
        :id="getRowValueId(row)"
        class="value"
      >
        <div
          v-if="row.status"
          :class="['status', row.status]"
        />
        <router-link
          v-if="row.value && row.to"
          :to="row.to"
          :data-testid="row.valueDataTestid"
        >
          {{ row.value }}
        </router-link>
        <span
          v-else-if="row.value"
          :data-testid="row.valueDataTestid"
          tabindex="0"
          :aria-label="row.value"
        >{{ row.value }}</span>
        <span
          v-else
          class="text-muted"
          :data-testid="row.valueDataTestid"
        >&mdash;</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.identifying-information {
  display: flex;
  flex-direction: column;
  min-width: 0;

  .metadata-row {
    display: grid;
    grid-template-columns: minmax(100px, 30%) minmax(0, 1fr);
    align-items: start;
    gap: 12px;
    margin: 0;
    padding: 8px 0;
    font-size: 14px;
    line-height: 1.5;

    &:first-child { padding-top: 0; }
    &:last-child { padding-bottom: 0; }

    .label {
      width: auto;
      min-width: 0;
      margin: 0;
      color: var(--dev-muted, var(--input-label));
      overflow-wrap: anywhere;
    }

    .value, .full-custom-value {
      min-width: 0;
      max-width: 100%;
      color: var(--dev-ink, var(--body-text));
      overflow-wrap: anywhere;
    }

    .value {
      display: flex;
      align-items: baseline;
      gap: 8px;
      white-space: normal;

      > div, > span, > a {
        min-width: 0;
        max-width: 100%;
        white-space: normal;
        overflow-wrap: anywhere;
      }
    }

    .status {
      display: inline-block;
      flex: 0 0 8px;
      border-radius: 50%;
      width: 8px;
      height: 8px;
      align-self: center;

      &.success { background-color: var(--success); }
      &.warning { background-color: var(--warning); }
      &.error { background-color: var(--error); }
      &.info { background-color: var(--info); }
    }
  }
}

@media (max-width: 480px) {
  .identifying-information .metadata-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
    padding: 10px 0;
  }
}
</style>
